import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, ChevronRight, Loader2, Play, RotateCcw,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { STAGE_COLORS, fmtMoney, timeAgo } from "@/lib/constants";
import { GUIDED_CLIENT_ID as CLIENT_ID, GUIDED_FLAG_ICON as Flag, STEPS } from "@/lib/guidedDemo";

export default function GuidedDemo({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setStep(0);
      api.get(`/clients/${CLIENT_ID}`).then((r) => setClient(r.data)).catch(() => {});
    }
  }, [open]);

  if (!client) return null;

  const s = STEPS[step];
  const alreadyComplete = client.workflow.crm_writeback && step === 0;
  const ranThisStep = s.ran ? s.ran(client) : true;
  const isLast = step === STEPS.length - 1;

  const runStep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${CLIENT_ID}/${s.action}`);
      setClient(data);
    } catch (e) {
      toast.error(apiError(e, "Step failed"));
    } finally {
      setBusy(false);
    }
  };

  const go = (fn) => {
    onOpenChange(false);
    fn();
  };

  const wf = client.workflow;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border border-zinc-700 text-zinc-100 max-w-2xl" data-testid="guided-demo-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-emerald-500 uppercase">
            <Flag className="w-3 h-3" strokeWidth={1.5} /> Guided Demo · Synthetic Demonstration Environment
          </div>
          <DialogTitle className="font-display text-xl text-zinc-100">{s.title}</DialogTitle>
          <DialogDescription className="sr-only">Step-by-step guided walkthrough of one complete O.N.E.Tech scenario on synthetic client ONET-1042.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5 mb-4" data-testid="guided-demo-progress">
          {STEPS.map((st, i) => (
            <div key={st.key} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-emerald-500" : "bg-zinc-800"}`} />
          ))}
        </div>

        {alreadyComplete ? (
          <div className="space-y-4" data-testid="guided-demo-complete-note">
            <p className="text-sm text-zinc-300 leading-relaxed">
              This scenario has already been run to completion — the outcome was written back to the CRM and attributed as recovered revenue.
              Reset the demo data to replay the full workflow from the first signal.
            </p>
            <button
              data-testid="guided-demo-open-profile"
              onClick={() => go(() => navigate(`/clients/${CLIENT_ID}`))}
              className="flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
            >
              Open client profile <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <s.icon className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
              <span className="text-[11px] font-mono tracking-widest text-emerald-400 uppercase">{s.phase}</span>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">{s.explain}</p>

            <div className="border border-zinc-800 rounded-sm bg-zinc-950/60 p-4 min-h-[96px] text-xs">
              {s.key === "intro" && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-zinc-100 text-sm">{client.name}</span>
                    <span className="font-mono text-zinc-500">{client.id}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${STAGE_COLORS[client.stage]}`}>{client.stage}</span>
                  </div>
                  <div className="text-zinc-500">{client.country} · {client.language} · {client.jurisdiction} · Est. value {fmtMoney(client.value)}</div>
                </div>
              )}
              {s.key === "detect" && wf.signal && (
                <div data-testid="guided-signal-result">
                  <div className="text-zinc-200">{wf.signal.text}</div>
                  <div className="text-[10px] font-mono text-zinc-600 mt-1">{wf.signal.source} · {timeAgo(wf.signal.ts)} · priority score now {client.score}</div>
                </div>
              )}
              {s.key === "identify" && (
                <div className="space-y-1.5" data-testid="guided-identify-result">
                  <div><span className="text-zinc-600">Stuck at: </span><span className="text-zinc-200">{client.stuck_at}</span></div>
                  <div><span className="text-zinc-600">Reason: </span><span className="text-zinc-200">{client.reason}</span></div>
                  <div><span className="text-zinc-600">Score: </span><span className="font-mono text-emerald-400">{client.score}/99</span>
                    <span className="text-zinc-600"> · Value: </span><span className="font-mono text-zinc-200">{fmtMoney(client.value)}</span></div>
                </div>
              )}
              {s.key === "decide" && wf.nba && (
                <div className="space-y-1.5" data-testid="guided-nba-result">
                  <div className="text-zinc-200">{wf.nba.action}</div>
                  <div className="text-[10px] font-mono text-emerald-500">Channel: {wf.nba.channel} · Language: {wf.nba.language}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{wf.nba.timing}</div>
                </div>
              )}
              {s.key === "govern" && wf.governance && (
                <div className="space-y-1.5" data-testid="guided-governance-result">
                  <div className="text-zinc-100 font-semibold">{wf.governance.decision}</div>
                  {wf.governance.checks.map((chk, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[11px] text-zinc-400">
                      {chk.passed ? <Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /> : <RotateCcw className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />}
                      {chk.check}
                    </div>
                  ))}
                </div>
              )}
              {s.key === "execute" && wf.execution === "executed" && (
                <div className="space-y-1.5" data-testid="guided-execute-result">
                  <div className="text-zinc-200">{client.communications[0]?.text}</div>
                  <div className="text-[10px] font-mono text-emerald-500">Delivered (simulated) · audit entry COMMUNICATION_EXECUTED created</div>
                </div>
              )}
              {s.key === "measure" && wf.outcome && (
                <div className="space-y-1.5" data-testid="guided-measure-result">
                  <div className="text-zinc-200">{wf.outcome}</div>
                  <div className="text-[10px] font-mono text-emerald-500">Stage advanced → {client.stage} · {fmtMoney(client.value)} attributed as recovered revenue on the dashboard</div>
                </div>
              )}
              {s.action && !ranThisStep && (
                <div className="text-zinc-600 italic">Step not run yet — press “Run this step”.</div>
              )}
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] font-mono text-zinc-600">Step {step + 1} of {STEPS.length}</span>
              <div className="flex gap-2">
                {isLast && ranThisStep ? (
                  <>
                    <button
                      data-testid="guided-demo-view-profile"
                      onClick={() => go(() => navigate(`/clients/${CLIENT_ID}`))}
                      className="px-4 py-2 rounded-sm text-xs font-semibold bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 transition-colors duration-200"
                    >
                      Open client profile
                    </button>
                    <button
                      data-testid="guided-demo-view-dashboard"
                      onClick={() => go(() => window.location.assign("/"))}
                      className="flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200"
                    >
                      See dashboard impact <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : s.action && !ranThisStep ? (
                  <button
                    data-testid="guided-demo-run-step"
                    onClick={runStep}
                    disabled={busy}
                    className="flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-zinc-950 transition-colors duration-200"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    Run this step
                  </button>
                ) : (
                  <button
                    data-testid="guided-demo-next"
                    onClick={() => setStep(step + 1)}
                    className="flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-zinc-950 transition-colors duration-200"
                  >
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
